'use client'

import { useCallback } from 'react'

import { buildAuthorizeUrl, venueCallbackUrl, waitForOauthCallback } from '@/lib/oauth/venue-oauth'
import { exchangeOauthCode, mintOauthState } from '@/service/venue.api'
import { TradeVenue } from '@/types/generated/common/types'
import type { OauthConfig } from '@/types/generated/public_api'

export interface VenueOauthConnectArgs {
  accountId: string
  venue: TradeVenue
  oauthConfig: OauthConfig
}

/**
 * Runs the shared exchange-OAuth link flow on an existing account: mint a fresh
 * single-use state nonce, open the venue authorize popup, wait for the redirect
 * to bounce back through /api/oauth/[venue], then exchange the one-time
 * code. Resolves once the backend accepts it (the account then moves
 * PENDING_AUTH -> ACTIVE); throws a user-facing Error on any failure.
 */
export function useVenueOauthConnect() {
  const connect = useCallback(async ({ accountId, venue, oauthConfig }: VenueOauthConnectArgs) => {
    const redirectUri = venueCallbackUrl(venue)
    // Open the window BEFORE any await: iOS Safari (and other strict blockers) only allow
    // `window.open` synchronously inside the tap, so opening it after minting the state —
    // a network round trip — got the popup blocked on iPhone. It starts blank and is
    // pointed at the venue once the state exists.
    const popup = window.open('', 'venue-oauth', 'popup,width=480,height=720')
    if (!popup) {
      throw new Error('The authorization popup was blocked. Please allow popups and try again.')
    }
    let state: string
    try {
      // Single-use, 5-minute TTL — minted right before the popup leaves for the venue.
      ;({ state } = await mintOauthState(venue, { accountId }))
    } catch (error) {
      popup.close()
      throw error
    }
    popup.location.href = buildAuthorizeUrl(oauthConfig, redirectUri, state)

    const { code } = await waitForOauthCallback(state)
    await exchangeOauthCode(venue, { accountId, code, state, redirectUri })
  }, [])

  return { connect }
}
